import type { Segment, SegmentSchedulingStatus, SegmentTaskInfo } from "../types";

export const SEGMENT_SCHEDULING_STATUS_OPTIONS: {
  value: SegmentSchedulingStatus;
  label: string;
}[] = [
  { value: "unscheduled", label: "Unscheduled" },
  { value: "scheduled", label: "Scheduled" },
  { value: "past_scheduled", label: "Past Due" },
  { value: "partially_scheduled", label: "Partially Scheduled" },
  { value: "completed", label: "Completed" },
  { value: "archived", label: "Removed" },
];

export const DEFAULT_SEGMENT_SCHEDULING_FILTERS: SegmentSchedulingStatus[] = [
  "unscheduled",
];

export function normalizeSegmentSchedulingStatus(
  value?: string | null,
): SegmentSchedulingStatus {
  const normalized = (value ?? "unscheduled").toLowerCase();
  if (
    normalized === "scheduled" ||
    normalized === "past_scheduled" ||
    normalized === "completed" ||
    normalized === "archived" ||
    normalized === "partially_scheduled"
  ) {
    return normalized;
  }
  return "unscheduled";
}

export function isSegmentDraggable(segment: Segment): boolean {
  const status = segment.schedulingStatus ?? "unscheduled";
  // Archived history does not lock the segment — status should be unscheduled when
  // only archived tasks remain, but allow drag defensively if archived is still set.
  return (
    status === "unscheduled" ||
    status === "past_scheduled" ||
    status === "partially_scheduled" ||
    status === "archived"
  );
}

export function isTaskDraggable(task: SegmentTaskInfo): boolean {
  return task.taskSchedulingStatus === "past_scheduled";
}

export function getSegmentDragHours(segment: Segment): number {
  if (segment.schedulingStatus === "partially_scheduled") {
    return segment.remainingHours ?? 0;
  }
  // Fully archived history → treat like unscheduled (full estimated hours).
  return segment.estimatedHours;
}

export interface SegmentStatusVisual {
  borderColor: string;
  backgroundColor: string;
  opacity: number;
  label: string;
  chipColor: "default" | "primary" | "warning" | "success" | "error";
}

export function getSegmentStatusVisual(
  status: SegmentSchedulingStatus,
): SegmentStatusVisual {
  switch (status) {
    case "scheduled":
      return {
        borderColor: "#1976d2",
        backgroundColor: "rgba(25, 118, 210, 0.04)",
        opacity: 1,
        label: "Scheduled",
        chipColor: "primary",
      };
    case "past_scheduled":
      return {
        borderColor: "#ed6c02",
        backgroundColor: "rgba(237, 108, 2, 0.06)",
        opacity: 1,
        label: "Past Due",
        chipColor: "warning",
      };
    case "completed":
      return {
        borderColor: "#2e7d32",
        backgroundColor: "rgba(46, 125, 50, 0.05)",
        opacity: 0.72,
        label: "Completed",
        chipColor: "success",
      };
    case "archived":
      return {
        borderColor: "#757575",
        backgroundColor: "rgba(117, 117, 117, 0.06)",
        opacity: 0.65,
        label: "Removed",
        chipColor: "default",
      };
    case "partially_scheduled":
      return {
        borderColor: "#0288d1",
        backgroundColor: "rgba(2, 136, 209, 0.05)",
        opacity: 1,
        label: "Partial",
        chipColor: "primary",
      };
    default:
      return {
        borderColor: "transparent",
        backgroundColor: "transparent",
        opacity: 1,
        label: "Unscheduled",
        chipColor: "default",
      };
  }
}

export function getTaskStatusVisual(taskStatus: string): SegmentStatusVisual {
  return getSegmentStatusVisual(
    normalizeSegmentSchedulingStatus(taskStatus === "pending" ? "scheduled" : taskStatus),
  );
}

export function getTaskStatusLabel(taskStatus: string): string {
  switch (taskStatus) {
    case "past_scheduled":
      return "Past Due";
    case "completed":
      return "Completed";
    case "archived":
      return "Removed";
    case "pending":
      return "Pending";
    default:
      return "Scheduled";
  }
}

export function getSegmentTaskSummary(tasks: SegmentTaskInfo[]): string {
  if (tasks.length === 0) return "";
  const counts = tasks.reduce<Record<string, number>>((acc, task) => {
    const key = task.taskSchedulingStatus || "pending";
    acc[key] = (acc[key] ?? 0) + 1;
    return acc;
  }, {});
  return Object.entries(counts)
    .map(([status, count]) => `${count} ${getTaskStatusLabel(status).toLowerCase()}`)
    .join(", ");
}

export function formatTaskWorkDate(workDate?: string): string {
  if (!workDate) return "No date";
  const datePart = workDate.split("T")[0];
  const [year, month, day] = datePart.split("-").map(Number);
  if (!year || !month || !day) return workDate;
  return new Date(year, month - 1, day).toLocaleDateString();
}

/** True when the date is today or a future local calendar day (time ignored). */
export function isDateTodayOrFuture(date: Date): boolean {
  if (!(date instanceof Date) || isNaN(date.getTime())) return false;
  const target = new Date(date);
  target.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return target.getTime() >= today.getTime();
}

/** Resolve TechnicianScheduleItem id from drag payload / local scheduled item id. */
export function resolveScheduleItemNumericId(
  itemId: string | number | undefined | null,
): number | undefined {
  if (itemId == null || itemId === "") return undefined;
  const raw = String(itemId);
  const match = raw.match(/(\d+)\s*$/);
  if (!match) return undefined;
  const parsed = Number(match[1]);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
}
